import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { Alert, Button, Flex, Modal, Row, Space, Typography } from 'antd';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import { QuestionTypeValues } from '../../../constants';
import { surveyDetailsType } from '../../../types';
import DraggableQuestion from './DraggableQuestion';
import DroppableCategory from './DroppableCategory';

const { Text, Title, Paragraph } = Typography;

const StyledFlex = styled(Flex)`
  padding-bottom: 12px;
`;

const StyledText = styled(Text)`
  font-size: 16px;
  font-weight: bold;
`;

function CategorizeQuestions({
  surveyData,
  setSurveyData,
  prev,
  next,
  availableTokens,
  createSurveyLoading,
}) {
  const [activeItem, setActiveItem] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor),
    useSensor(MouseSensor), // required for testing
  );

  const renameQuestion = ({ updatedName, index }) => {
    const updatedSurveyData = { ...surveyData };
    updatedSurveyData.questions[index].updated_question_name = updatedName;
    setSurveyData(updatedSurveyData);
  };

  const onFinish = () => {
    const totalOEResponses = surveyData.questions
      .filter((question) => question.columnType === QuestionTypeValues.HAS_THEMES)
      .reduce((sum, question) => sum + question.responseCount, 0);

    if (totalOEResponses > availableTokens) {
      Modal.error({
        title: 'Insufficient tokens',
        content: (
          <>
            <Paragraph>
              You currently have {availableTokens} tokens, but this survey requires{' '}
              {totalOEResponses} tokens.
            </Paragraph>
            <Paragraph>
              Please contact <a href="mailto:support@fathomthat.ai">support@fathomthat.ai</a>, and
              we&apos;ll get you topped up fast so you can keep going!
            </Paragraph>
          </>
        ),
      });
    } else if (
      surveyData.questions.filter((question) => question.columnType === QuestionTypeValues.IS_KEY)
        .length > 1
    ) {
      Modal.error({
        title: 'Multiple respondent ids',
        content:
          'Multiple questions/columns were designated as containing respondent ids. Please only select one question/column containing respondent ids',
        okText: 'Back to question types',
      });
    } else if (
      !surveyData.questions.some(
        (question) => question.columnType === QuestionTypeValues.HAS_THEMES,
      )
    ) {
      Modal.error({
        title: 'Please designate the question(s) you would like us to develop themes for.',
        content:
          'Your file must contain at least one open ended question for which you would like to create themes in order to complete the upload.',
      });
    } else if (surveyData.questions.some((question) => question.updated_question_name === '')) {
      Modal.error({
        title: 'Missing question text',
        content: 'Please provide text for all questions before proceeding.',
      });
    } else if (
      !surveyData.questions.some(
        (question) =>
          question.columnType === QuestionTypeValues.IS_SINGLE_SELECT ||
          question.columnType === QuestionTypeValues.IS_MULTI_SELECT,
      )
    ) {
      Modal.confirm({
        title:
          "It looks like you haven't designated any questions you would like to use as filters.",
        content:
          "Please note that if you don't designate any filters, you won't be able to segment the themes.",
        okText: 'Designate filters',
        cancelText: 'Upload anyways',
        onCancel: () => next(),
      });
    } else if (
      !surveyData.questions.some((question) => question.columnType === QuestionTypeValues.IS_KEY)
    ) {
      Modal.confirm({
        title: 'No ID column selected',
        content:
          "You won't be able to designate an ID column after upload. If you may need to merge results with other data, include and select a unique ID column before continuing.",
        okText: 'Set ID column',
        cancelText: 'Upload anyways',
        onCancel: () => next(),
      });
    } else {
      next();
    }
  };

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveItem(active.id);
  };

  const handleDragEnd = (event) => {
    const { over } = event;

    if (!over) {
      setActiveItem(null);
      return;
    }

    const activeQuestion = surveyData.questions.find((item) => item.index === activeItem);

    if (activeQuestion.columnType !== over.id) {
      const updatedQuestions = surveyData.questions.map((question) => {
        if (question.index === activeItem) {
          return {
            ...question,
            columnType: over.id,
          };
        }
        return question;
      });
      const updatedSurveyData = { ...surveyData, questions: updatedQuestions };
      setSurveyData(updatedSurveyData);
    }
    setActiveItem(null);
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <Title level={3}>Categorize your columns</Title>
      <Paragraph type="secondary">
        Make sure each column is assigned to the correct category so your data is processed
        accurately.
      </Paragraph>
      <Paragraph type="secondary">
        To move a column, click to the left of its name and drag it to the correct category.
      </Paragraph>
      <StyledFlex vertical gap={24}>
        <Space direction="vertical">
          <StyledText>Open-ended questions</StyledText>
          <Text type="secondary">
            Fathom will code responses in these columns. Make sure each name includes the full
            question text.
          </Text>
          <DroppableCategory
            categoryId={QuestionTypeValues.HAS_THEMES}
            questions={surveyData.questions?.filter(
              (q) => q.columnType === QuestionTypeValues.HAS_THEMES,
            )}
            renameQuestion={renameQuestion}
          />
        </Space>
        <Space direction="vertical">
          <StyledText>Single-select questions</StyledText>
          <Text type="secondary">
            These will be used for segmentation and statistical analysis.
          </Text>
          <DroppableCategory
            categoryId={QuestionTypeValues.IS_SINGLE_SELECT}
            questions={surveyData.questions?.filter(
              (q) => q.columnType === QuestionTypeValues.IS_SINGLE_SELECT,
            )}
            renameQuestion={renameQuestion}
          />
        </Space>
        <Space direction="vertical">
          <StyledText>Multi-select questions</StyledText>
          <Text type="secondary">
            These will be used for segmentation and statistical analysis.
          </Text>
          <Alert
            message={
              <>
                <b>Note</b>: In your CSV, multi-select choices must be separated using <i>pipes</i>.
              </>
            }
            type="info"
          />
          <DroppableCategory
            categoryId={QuestionTypeValues.IS_MULTI_SELECT}
            questions={surveyData.questions?.filter(
              (q) => q.columnType === QuestionTypeValues.IS_MULTI_SELECT,
            )}
            renameQuestion={renameQuestion}
          />
        </Space>
        <Space direction="vertical">
          <StyledText>ID column (optional)</StyledText>
          <Text type="secondary">A unique identifier for each respondent.</Text>
          <DroppableCategory
            categoryId={QuestionTypeValues.IS_KEY}
            questions={surveyData.questions?.filter(
              (q) => q.columnType === QuestionTypeValues.IS_KEY,
            )}
            renameQuestion={renameQuestion}
          />
        </Space>
        <Space direction="vertical">
          <StyledText>Other columns you want in your exports</StyledText>
          <Text type="secondary">
            These won&apos;t be used by Fathom, but will be included in export so you don&apos;t
            need to merge them later.
          </Text>
          <DroppableCategory
            categoryId={QuestionTypeValues.IS_METADATA}
            questions={surveyData.questions?.filter(
              (q) => q.columnType === QuestionTypeValues.IS_METADATA,
            )}
            renameQuestion={renameQuestion}
          />
        </Space>
        <Space direction="vertical">
          <StyledText>Do not upload</StyledText>
          <Text type="secondary">
            These won&apos;t be uploaded to Fathom and won&apos;t be included in your export.
          </Text>
          <DroppableCategory
            categoryId={QuestionTypeValues.REMOVE}
            questions={surveyData.questions?.filter(
              (q) => q.columnType === QuestionTypeValues.REMOVE,
            )}
            renameQuestion={renameQuestion}
          />
        </Space>
      </StyledFlex>
      <Row justify="end">
        <Space>
          <Button onClick={prev} disabled={createSurveyLoading}>
            Back
          </Button>
          <Button
            type="primary"
            onClick={onFinish}
            loading={createSurveyLoading}
            disabled={createSurveyLoading}
          >
            Continue
          </Button>
        </Space>
      </Row>
      <DragOverlay>
        {activeItem ? (
          <DraggableQuestion
            id={activeItem}
            key={activeItem}
            text={
              surveyData.questions.find((item) => item.index === activeItem).updated_question_name
            }
            renameQuestion={renameQuestion}
            responseCount={
              surveyData.questions.find((item) => item.index === activeItem).responseCount
            }
            popOverResponses={
              surveyData.questions.find((item) => item.index === activeItem).popOverResponses
            }
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

CategorizeQuestions.propTypes = {
  surveyData: surveyDetailsType.isRequired,
  prev: PropTypes.func.isRequired,
  next: PropTypes.func.isRequired,
  setSurveyData: PropTypes.func.isRequired,
  availableTokens: PropTypes.number.isRequired,
  createSurveyLoading: PropTypes.bool.isRequired,
};

export default CategorizeQuestions;
